import {
    memo,
    type PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    DeleteOutlined,
    EditOutlined,
    ExpandAltOutlined,
    FullscreenExitOutlined,
    LeftOutlined,
    MenuUnfoldOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { Layout } from 'antd';
import classNames from 'classnames';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';

import { useAutoLoadPatterns } from '@/features/load-patterns';
import {
    addStudiedPattern,
    updateStudiedPatternColor,
    updateStudiedPatternName,
} from '@/features/pattern-lab';

import {
    type Pattern,
    type PatternFormValues,
    PatternModal,
    useDeletePatternMutation,
    useToggleFlagFieldMutation,
    useUpdatePatternMutation,
} from '@/entities/pattern';
import {
    EmptyPatternWatchlist,
    PatternWatchlistSelector,
    PatternWatchlistTitle,
    useAutoSelectWatchlist,
    useGetPatternWatchlistsQuery,
    useSelectedPatternWatchlistId,
} from '@/entities/pattern-watchlist';

import { getPatternStudioLabPath } from '@/shared/constants/router';
import { useAntMessage } from '@/shared/lib/hooks';
import { logError } from '@/shared/lib/logging';
import { roundToDecimals } from '@/shared/lib/math';
import { useAppDispatch } from '@/shared/lib/store';
import { Button } from '@/shared/ui/button';
import { ExpandablePatternId } from '@/shared/ui/expandable-pattern-id';
import { Popover } from '@/shared/ui/popover';
import {
    ConfigurableTable,
    createColorColumnFormatter,
    createDirectionTypeFormatter,
    createFlagColumnFormatter,
    createPercentageBadgeFormatter,
    createRoundedNumericColumns,
    useTableColumnsConfig,
} from '@/shared/ui/table';

import cls from './pattern-studio-sider.module.css';

function Portal({ children }: PropsWithChildren): JSX.Element | null {
    const [isClientSide, setIsClientSide] = useState(false);

    useEffect(() => {
        setIsClientSide(true);

        return () => setIsClientSide(false);
    }, []);

    if (!isClientSide) return null;

    return createPortal(children, document.body);
}

const TABLE_ID = 'MY_AI_PATTERN';

interface PatternStudioSiderProps {
    isCollapsed: boolean;
    setIsCollapsed: (value: boolean) => void;
}

export const PatternStudioSider = memo(
    ({ isCollapsed, setIsCollapsed }: PatternStudioSiderProps) => {
        const dispatch = useAppDispatch();
        const navigate = useNavigate();
        const [isFullscreen, setIsFullscreen] = useState(false);
        const [editingPattern, setEditingPattern] = useState<Pattern | null>(
            null,
        );
        const { success } = useAntMessage();
        const siderRef = useRef<HTMLDivElement>(null);
        const { t } = useTranslation(['pattern-studio', 'table', 'pattern']);
        const selectedPatternWatchlistId = useSelectedPatternWatchlistId();

        const {
            isOpen: isColumnsConfigOpen,
            onClose: onColumnsConfigClose,
            toggle: toggleColumnsConfig,
        } = useTableColumnsConfig();

        // Second config state for fullscreen mode
        const {
            isOpen: isFullscreenColumnsConfigOpen,
            onClose: onFullscreenColumnsConfigClose,
            toggle: toggleFullscreenColumnsConfig,
        } = useTableColumnsConfig();

        const {
            patterns,
            isLoading: isPatternsLoading,
            isFetching: isPatternsFetching,
        } = useAutoLoadPatterns();
        const { data: watchlists = [] } = useGetPatternWatchlistsQuery();
        const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
        const [pendingEditIds, setPendingEditIds] = useState<number[]>([]);
        const [deletePattern, deletePatternResult] = useDeletePatternMutation();
        const [updatePattern, updatePatternResult] = useUpdatePatternMutation();
        const [toggleFlagField] = useToggleFlagFieldMutation();

        useAutoSelectWatchlist();

        const isTableLoading =
            isPatternsLoading ||
            isPatternsFetching ||
            deletePatternResult.isLoading ||
            updatePatternResult.isLoading ||
            pendingDeleteIds.length > 0 ||
            pendingEditIds.length > 0;

        const isPatternDeleting = useCallback(
            (patternId: number) =>
                (deletePatternResult.isLoading &&
                    deletePatternResult.originalArgs === patternId) ||
                pendingDeleteIds.includes(patternId),
            [deletePatternResult, pendingDeleteIds],
        );

        const isPatternEditing = useCallback(
            (patternId: number) =>
                (updatePatternResult.isLoading &&
                    updatePatternResult.originalArgs?.id === patternId) ||
                pendingEditIds.includes(patternId),
            [updatePatternResult, pendingEditIds],
        );

        const handleFlagChange = useCallback(
            async (id: number, checked: boolean) => {
                if (!selectedPatternWatchlistId) return;

                try {
                    await toggleFlagField({
                        id,
                        flag_field: checked,
                    });
                } catch (error) {
                    logError('Failed to toggle pattern flag:', error);
                }
            },
            [selectedPatternWatchlistId, toggleFlagField],
        );

        const handleGoClick = useCallback(
            (pattern: Pattern) => {
                dispatch(
                    addStudiedPattern({
                        pattern,
                        color: pattern.color,
                        name: pattern.pattern_name,
                    }),
                );
                navigate(getPatternStudioLabPath());
            },
            [dispatch, navigate],
        );

        const handleDelete = useCallback(
            async (id: number) => {
                try {
                    setPendingDeleteIds((prev) => [...prev, id]);

                    await deletePattern(id);

                    // Remove from studied patterns if present
                    const deleted = patterns?.find((p) => p.id === id);

                    if (deleted) {
                        // Reset color and nickname to default values
                        dispatch(
                            updateStudiedPatternColor({
                                color: undefined,
                                patternUniqueId: deleted.unique_id,
                            }),
                        );

                        dispatch(
                            updateStudiedPatternName({
                                name: '',
                                patternUniqueId: deleted.unique_id,
                            }),
                        );
                    }

                    success?.({
                        content: t('pattern:deleteSuccess'),
                    });

                    setPendingDeleteIds((prev) =>
                        prev.filter((pendingId) => pendingId !== id),
                    );
                } catch (error) {
                    logError('Failed to delete pattern:', error);
                    setPendingDeleteIds((prev) =>
                        prev.filter((pendingId) => pendingId !== id),
                    );
                }
            },
            [deletePattern, success, t, patterns, dispatch],
        );

        const handleEdit = useCallback((pattern: Pattern) => {
            setEditingPattern(pattern);
        }, []);

        const handleEditClose = useCallback(() => {
            setEditingPattern(null);
        }, []);

        const handleEditSubmit = useCallback(
            async (values: PatternFormValues) => {
                if (!editingPattern || !selectedPatternWatchlistId) return;

                try {
                    setPendingEditIds((prev) => [...prev, editingPattern.id]);

                    await updatePattern({
                        id: editingPattern.id,
                        pattern_name: values.pattern_name,
                        color: values.color,
                    });

                    dispatch(
                        updateStudiedPatternColor({
                            color: values.color,
                            patternUniqueId: editingPattern.unique_id,
                        }),
                    );

                    dispatch(
                        updateStudiedPatternName({
                            name: values.pattern_name,
                            patternUniqueId: editingPattern.unique_id,
                        }),
                    );

                    setEditingPattern(null);

                    setPendingEditIds((prev) =>
                        prev.filter(
                            (pendingId) => pendingId !== editingPattern.id,
                        ),
                    );
                } catch (error) {
                    logError('Failed to update pattern:', error);
                    setPendingEditIds((prev) =>
                        prev.filter(
                            (pendingId) => pendingId !== editingPattern.id,
                        ),
                    );
                }
            },
            [
                editingPattern,
                updatePattern,
                selectedPatternWatchlistId,
                dispatch,
            ],
        );

        const extendedColumns = useMemo(
            () => ({
                flag: {
                    title: '',
                    render: createFlagColumnFormatter<Pattern>(
                        (record) => record.flag_field ?? false,
                        handleFlagChange,
                        (record) => record.id,
                        (record) => record.color,
                    ),
                    disableVisibilityToggle: true,
                    order: -2,
                },
                color: {
                    title: t('table:columns.color'),
                    key: 'color',
                    render: createColorColumnFormatter<Pattern>(
                        (record) => record.color,
                    ),
                },
                direction_type: {
                    key: 'direction_type',
                    render: createDirectionTypeFormatter<Pattern>(
                        (record: Pattern) => record.direction_type,
                    ),
                },
                price_drawdown_post_mean: {
                    key: 'price_drawdown_post_mean',
                    render: createPercentageBadgeFormatter<Pattern>(
                        (record: Pattern) => record.price_drawdown_post_mean,
                        {
                            shouldShowSign: false,
                            shouldUseColorsBasedOnSign: false,
                            shouldShowDirection: false,
                        },
                    ),
                },
                price_threshold_post_mean: {
                    key: 'price_threshold_post_mean',
                    render: createPercentageBadgeFormatter<Pattern>(
                        (record: Pattern) =>
                            roundToDecimals(
                                record.price_threshold_post_mean,
                                2,
                            ),
                        {
                            shouldShowSign: false,
                            shouldUseColorsBasedOnSign: false,
                            isAbsoluteValue: true,
                            shouldShowDirection: false,
                            precision: 2,
                        },
                    ),
                },
                unique_id: {
                    key: 'unique_id',
                    render: (_: unknown, record: Pattern) => (
                        <ExpandablePatternId uniqueId={record.unique_id} />
                    ),
                },
                price_reg_post_mean: {
                    key: 'price_reg_post_mean',
                    render: (_: unknown, record: Pattern) =>
                        `${roundToDecimals(Math.abs(record.price_reg_post_mean))}%`,
                },
                algotech_index: {
                    key: 'algotech_index',
                    render: (_: unknown, record: Pattern) =>
                        `${roundToDecimals(record.algotech_index, 1)}/100`,
                },
                price_efficiency_post_mean: {
                    key: 'price_efficiency_post_mean',
                    render: (_: unknown, record: Pattern) =>
                        `${roundToDecimals(record.price_efficiency_post_mean * 100, 2)}%`,
                },
                go: {
                    title: t('table:columns.go'),
                    key: 'go',
                    render: (_: unknown, record: Pattern) => (
                        <Button
                            onClick={() => handleGoClick(record)}
                            title={t('actions.go')}
                        />
                    ),
                    disableVisibilityToggle: true,
                    order: 50,
                },
                edit: {
                    title: '',
                    render: (_: unknown, record: Pattern) => {
                        const isEditing = isPatternEditing(record.id);

                        return (
                            <Button
                                icon={<EditOutlined />}
                                isLoading={isEditing}
                                onClick={() => handleEdit(record)}
                                variant="ghost"
                            />
                        );
                    },
                    disableVisibilityToggle: true,
                    order: 52,
                },
                delete: {
                    title: '',
                    render: (_: unknown, record: Pattern) => {
                        const isDeleting = isPatternDeleting(record.id);

                        return (
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                isLoading={isDeleting}
                                onClick={() => handleDelete(record.id)}
                                variant="ghost"
                            />
                        );
                    },
                    disableVisibilityToggle: true,
                    order: 53,
                },
                ...createRoundedNumericColumns<Pattern>([
                    'price_candles_to_drawdown_post_mean',
                    'price_candles_to_max_post_mean',
                    'price_candles_to_min_post_mean',
                    'price_candles_to_threshold_down_post_mean',
                    'price_candles_to_threshold_up_post_mean',
                ]),
            }),
            [
                t,
                handleDelete,
                handleFlagChange,
                isPatternDeleting,
                handleGoClick,
                handleEdit,
                isPatternEditing,
            ],
        );

        const handleMouseMove = useCallback(
            (e: React.MouseEvent<HTMLDivElement>) => {
                if (!siderRef.current) return;

                const rect = siderRef.current.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;

                siderRef.current.style.setProperty('--mouse-x', `${x}%`);
                siderRef.current.style.setProperty('--mouse-y', `${y}%`);
            },
            [],
        );

        const onChangeCollapse = () => {
            const isNewCollapsed = !isCollapsed;

            setIsCollapsed(isNewCollapsed);
        };

        const toggleFullscreen = () => {
            setIsFullscreen(!isFullscreen);

            // Reset config popover states when toggling fullscreen
            if (isColumnsConfigOpen) {
                onColumnsConfigClose();
            }

            if (isFullscreenColumnsConfigOpen) {
                onFullscreenColumnsConfigClose();
            }
        };

        const renderTable = (fullscreenMode = false) => (
            <div className={cls.tableWrapper}>
                <ConfigurableTable
                    className={cls.patternsTable}
                    dataSource={patterns ?? []}
                    emptyDescription={t('emptyState.watchlist.zeroPattern')}
                    extendedColumns={extendedColumns}
                    isConfigOpen={
                        fullscreenMode
                            ? isFullscreenColumnsConfigOpen
                            : isColumnsConfigOpen
                    }
                    loading={isTableLoading}
                    onConfigClose={
                        fullscreenMode
                            ? onFullscreenColumnsConfigClose
                            : onColumnsConfigClose
                    }
                    pagination={false}
                    rowKey="unique_id"
                    size="small"
                    tableId={TABLE_ID}
                />
            </div>
        );

        const renderFullscreenToggleButton = () => {
            if (watchlists.length === 0) return null;

            return (
                <Button
                    icon={
                        isFullscreen ? (
                            <FullscreenExitOutlined />
                        ) : (
                            <ExpandAltOutlined />
                        )
                    }
                    onClick={toggleFullscreen}
                    variant="ghost"
                />
            );
        };

        const renderHeader = () => (
            <motion.div
                className={cls.header}
                layoutId="pattern-header"
            >
                <div className={cls.headerContent}>
                    <PatternWatchlistTitle onClick={onChangeCollapse} />
                </div>
                <motion.div layoutId="pattern-header-button">
                    <AnimatePresence mode="wait">
                        {renderFullscreenToggleButton()}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        );

        const renderFooter = () => (
            <motion.div
                className={cls.footer}
                layoutId="pattern-footer"
            >
                <Button
                    icon={isCollapsed ? <LeftOutlined /> : <RightOutlined />}
                    onClick={onChangeCollapse}
                    variant="secondary"
                />
            </motion.div>
        );

        const renderColumnConfigButton = (fullscreenMode = false) => {
            const columnConfigContent = (
                <div className={cls.columnConfig}>
                    <ConfigurableTable
                        extendedColumns={extendedColumns}
                        isConfigOnly
                        tableId={TABLE_ID}
                    />
                </div>
            );

            return (
                <Popover
                    content={columnConfigContent}
                    onOpenChange={(open) => {
                        if (!open) {
                            if (fullscreenMode) {
                                onFullscreenColumnsConfigClose();
                            } else {
                                onColumnsConfigClose();
                            }
                        }
                    }}
                    open={
                        fullscreenMode
                            ? isFullscreenColumnsConfigOpen
                            : isColumnsConfigOpen
                    }
                    trigger={
                        <Button
                            icon={<MenuUnfoldOutlined />}
                            onClick={
                                fullscreenMode
                                    ? toggleFullscreenColumnsConfig
                                    : toggleColumnsConfig
                            }
                        />
                    }
                />
            );
        };

        return (
            <LayoutGroup>
                <Layout.Sider
                    ref={siderRef}
                    className={classNames(cls.root, {
                        [cls.hidden]: isFullscreen,
                        [cls.collapsed]: isCollapsed,
                    })}
                    collapsed={isCollapsed}
                    collapsedWidth={56}
                    data-tour="pattern-studio-watchlist"
                    onMouseMove={handleMouseMove}
                    width={408}
                >
                    <motion.div
                        className={cls.siderContent}
                        id="PatternStudioSider"
                        layoutId="pattern-layout"
                    >
                        {renderHeader()}

                        {watchlists.length > 0 ? (
                            <>
                                <div className={cls.actionContainer}>
                                    <div
                                        style={{
                                            flexGrow: 1,
                                            display: 'inline-block',
                                        }}
                                    >
                                        <PatternWatchlistSelector />
                                    </div>
                                    {renderColumnConfigButton(false)}
                                </div>
                                {renderTable(false)}
                            </>
                        ) : (
                            <EmptyPatternWatchlist />
                        )}
                    </motion.div>
                    <AnimatePresence>
                        <motion.div
                            animate={{ opacity: isFullscreen ? 0 : 1 }}
                            initial={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                        >
                            {renderFooter()}
                        </motion.div>
                    </AnimatePresence>
                </Layout.Sider>

                <AnimatePresence>
                    {isFullscreen && (
                        <Portal>
                            <div className="pattern-studio-overlay">
                                <motion.div
                                    animate={{ backdropFilter: 'blur(2px)' }}
                                    className="pattern-studio-backdrop"
                                    exit={{ backdropFilter: 'blur(0px)' }}
                                    initial={{ backdropFilter: 'blur(0px)' }}
                                />
                                <div className="pattern-studio-content-wrapper">
                                    <motion.div
                                        className="pattern-studio-content"
                                        layoutId="pattern-layout"
                                    >
                                        {renderHeader()}
                                        {watchlists.length > 0 ? (
                                            <>
                                                <div
                                                    className={
                                                        cls.actionSpacebetween
                                                    }
                                                >
                                                    <PatternWatchlistSelector />
                                                    <div
                                                        className={cls.buttons}
                                                    >
                                                        {renderColumnConfigButton(
                                                            true,
                                                        )}
                                                    </div>
                                                </div>
                                                {renderTable(true)}
                                            </>
                                        ) : (
                                            <EmptyPatternWatchlist />
                                        )}
                                    </motion.div>
                                </div>
                            </div>
                        </Portal>
                    )}
                </AnimatePresence>

                {editingPattern && (
                    <PatternModal
                        data={{
                            pattern_name: editingPattern.pattern_name!,
                            color: editingPattern.color!,
                        }}
                        isLoading={updatePatternResult.isLoading}
                        isOpen={!!editingPattern}
                        mode="edit"
                        onClose={handleEditClose}
                        onSubmit={handleEditSubmit}
                    />
                )}
            </LayoutGroup>
        );
    },
);

PatternStudioSider.displayName = 'PatternStudioSider';
